import React, { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { useNavigate } from "react-router-dom";
import { get } from "../services";

/** Popular cities: GET /api/vendors/popular-cities/top/{limit} — not paginated; no query params. */
const POPULAR_CITIES_TOP_LIMIT = 10;
const DEFAULT_CITY_CARD_IMAGE =
  "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=400&h=240&fit=crop";

// Category icon component helper
const CategoryIcon = ({ categoryName }: { categoryName: string }) => {
  const iconMap: Record<string, React.ReactElement> = {
    "Catering Services": (
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3 2V8C3 9.65685 4.34315 11 6 11H7.5C8.32843 11 9 11.6716 9 12.5V13H15V12.5C15 11.6716 15.6716 11 16.5 11H18C19.6569 11 21 9.65685 21 8V2"
          stroke="#6C63FF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M3 6H21"
          stroke="#6C63FF"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M7 12V22H17V12"
          stroke="#6C63FF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9 22V18H15V22"
          stroke="#6C63FF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    Photography: (
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 4H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z"
          stroke="#6C63FF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <circle
          cx="12"
          cy="13"
          r="4"
          stroke="#6C63FF"
          strokeWidth="2"
          fill="none"
        />
      </svg>
    ),
    "Music & DJ": (
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M9 18V5L21 3V16"
          stroke="#6C63FF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <circle
          cx="6"
          cy="18"
          r="3"
          stroke="#6C63FF"
          strokeWidth="2"
          fill="none"
        />
        <circle
          cx="18"
          cy="16"
          r="3"
          stroke="#6C63FF"
          strokeWidth="2"
          fill="none"
        />
      </svg>
    ),
    "Decor & Design": (
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 2L2 7L12 12L22 7L12 2Z"
          stroke="#6C63FF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <path
          d="M2 17L12 22L22 17"
          stroke="#6C63FF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <path
          d="M2 12L12 17L22 12"
          stroke="#6C63FF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    ),
    Videography: (
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M23 7L16 12L23 17V7Z"
          stroke="#6C63FF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <path
          d="M14 5H3C1.89543 5 1 5.89543 1 7V17C1 18.1046 1.89543 19 3 19H14C15.1046 19 16 18.1046 16 17V7C16 5.89543 15.1046 5 14 5Z"
          stroke="#6C63FF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    ),
    Transport: (
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1 3H5L7.68 14.39C7.77144 14.8504 8.02191 15.264 8.38755 15.5583C8.75318 15.8526 9.2107 16.009 9.68 16H19.4C19.8693 16.009 20.3268 15.8526 20.6925 15.5583C21.0581 15.264 21.3086 14.8504 21.4 14.39L23 8H6"
          stroke="#6C63FF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <circle
          cx="7"
          cy="20"
          r="2"
          stroke="#6C63FF"
          strokeWidth="2"
          fill="none"
        />
        <circle
          cx="17"
          cy="20"
          r="2"
          stroke="#6C63FF"
          strokeWidth="2"
          fill="none"
        />
      </svg>
    ),
    "Event Planning & Coordinators": (
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15"
          stroke="#6C63FF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <path
          d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V5H9V5Z"
          stroke="#6C63FF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <path
          d="M9 12H15M9 16H15"
          stroke="#6C63FF"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
    "Venue Management with Event Production Services": (
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3 21H21"
          stroke="#6C63FF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5 21V7L13 2L21 7V21"
          stroke="#6C63FF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <path
          d="M9 9V13H15V9"
          stroke="#6C63FF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    ),
  };

  return (
    <div
      style={{
        width: 48,
        height: 48,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 12px",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        borderRadius: "12px",
        boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
      }}
    >
      {iconMap[categoryName] || (
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="#6C63FF"
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M12 8V12L15 15"
            stroke="#6C63FF"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
};

// Sub-categories mapping for each main category
const categorySubcategories: Record<string, string[]> = {
  "Catering Services": [
    "Buffet",
    "Cocktail Service",
    "Wait Staff",
    "Bar Service",
    "Custom Menus",
  ],
  Photography: [
    "Event Photography",
    "Portraits",
    "Photo Booth",
    "Drone Photography",
  ],
  "Music & DJ": ["DJ", "Live Band", "Sound System", "MC Services"],
  "Decor & Design": [
    "Floral Design",
    "Stage Decor",
    "Table Settings",
    "Lighting",
  ],
  Videography: ["Event Videography", "Highlight Reels", "Live Streaming"],
  Transport: ["Guest Shuttles", "VIP Transport", "Logistics"],
  "Event Planning & Coordinators": [
    "Full Planning",
    "Day-of Coordination",
    "Vendor Management",
  ],
  "Venue Management with Event Production Services": [
    "Venue Booking",
    "Stage Design",
    "Lighting & Sound",
    "Event Logistics",
    "On-site Coordination",
  ],
  // Add more as needed
};

// Helper to transform category names for display
function transformCategoryName(categoryName: string): string {
  if (categoryName === "Venue Sourcing & Management") {
    return "Venue Management with Event Production Services";
  }
  return categoryName;
}

// Helper to fetch full vendor details and navigate
async function handleEngageVendor(
  vendorId: number,
  navigate: any,
  setLoadingVendor: any
) {
  setLoadingVendor(true);
  try {
    const data = await get(`/api/vendors/${vendorId}/full-details`);
    if (data && data.data) {
      navigate(`/business-listing/${vendorId}`, { state: { biz: data.data } });
    } else {
      alert("Vendor details not found.");
    }
  } catch (err) {
    alert("Failed to load vendor details.");
  } finally {
    setLoadingVendor(false);
  }
}

function Home() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [paidAdverts, setPaidAdverts] = useState<any[]>([]);
  const [loadingAdverts, setLoadingAdverts] = useState(true);
  const [advertsError, setAdvertsError] = useState<string | null>(null);
  const navigate = useNavigate();
  // Categories state
  const [categories, setCategories] = useState<any[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);

  // Featured event vendors state (moved inside component)
  const [featuredVendors, setFeaturedVendors] = useState<any[]>([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const [featuredError, setFeaturedError] = useState<string | null>(null);

  // Loading state for Engage Vendor button
  const [loadingVendor, setLoadingVendor] = useState(false);

  useEffect(() => {
    const fetchAdverts = async () => {
      setLoadingAdverts(true);
      setAdvertsError(null);
      try {
        // Use the correct endpoint for active adverts
        const data = await get("/api/adverts/active");
        // The API returns { data: { adverts: [...] } }
        const adverts = data?.data?.adverts || data?.adverts || [];

        if (Array.isArray(adverts) && adverts.length > 0) {
          // Prioritize currently active adverts, but also show default adverts if no active ones
          const currentlyActive = adverts.filter(
            (ad: any) => ad.isCurrentlyActive === true
          );
          const defaultAdverts = adverts.filter(
            (ad: any) => ad.isDefault === true
          );

          // Show currently active adverts if available, otherwise show default adverts
          setPaidAdverts(
            currentlyActive.length > 0 ? currentlyActive : defaultAdverts
          );
        } else {
          setPaidAdverts([]);
        }
      } catch (err: any) {
        setAdvertsError("Failed to load adverts. Please try again later.");
        setPaidAdverts([]);
      } finally {
        setLoadingAdverts(false);
      }
    };
    fetchAdverts();
    // Fetch categories
    const fetchCategories = async () => {
      setLoadingCategories(true);
      setCategoriesError(null);
      try {
        const data = await get("/api/categories");
        if (data && Array.isArray(data.data)) {
          setCategories(data.data);
        } else {
          setCategories([]);
        }
      } catch (err: any) {
        setCategoriesError(
          "Failed to load categories. Please try again later."
        );
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  // Top rated vendors state
  const [topRatedVendors, setTopRatedVendors] = useState<any[]>([]);
  const [loadingTopRated, setLoadingTopRated] = useState(true);
  const [topRatedError, setTopRatedError] = useState<string | null>(null);

  // Testimonials state
  const [testimonials, setTestimonials] = useState<any[]>([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);
  const [testimonialsError, setTestimonialsError] = useState<string | null>(
    null
  );

  // Recent events state
  const [recentEvents, setRecentEvents] = useState<any[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const resolveEventImage = (event: any) => {
    const candidates = [
      typeof event?.image === "string" ? event.image : "",
      typeof event?.image?.url === "string" ? event.image.url : "",
      event?.imageUrl,
      event?.coverImage,
      event?.bannerImage,
      Array.isArray(event?.images) ? event.images[0] : "",
    ];
    const validSrc = candidates.find(
      (src) => typeof src === "string" && src.trim().length > 0
    );
    return validSrc || null;
  };

  // Dynamic popular cities state
  const [popularCities, setPopularCities] = useState<any[]>([]);
  const [loadingCities, setLoadingCities] = useState(true);
  const [citiesError, setCitiesError] = useState<string | null>(null);

  // Fetch popular cities from backend
  const fetchPopularCities = async () => {
    setLoadingCities(true);
    setCitiesError(null);
    try {
      const data = await get(
        `/api/vendors/popular-cities/top/${POPULAR_CITIES_TOP_LIMIT}`
      );
      if (data && Array.isArray(data.data)) {
        setPopularCities(data.data);
      } else {
        setPopularCities([]);
      }
    } catch (err: any) {
      setCitiesError("Failed to load cities.");
      setPopularCities([]);
    } finally {
      setLoadingCities(false);
    }
  };
  useEffect(() => {
    fetchPopularCities();
  }, []);

  // Fetch top rated vendors
  const fetchTopRatedVendors = async () => {
    setLoadingTopRated(true);
    setTopRatedError(null);
    try {
      const data = await get(
        "/api/vendors/top-rated?limit=&featuredOnly=false"
      );
      if (data && Array.isArray(data.data)) {
        setTopRatedVendors(data.data);
      } else {
        setTopRatedVendors([]);
      }
    } catch (err: any) {
      setTopRatedError("Failed to load top rated vendors.");
      setTopRatedVendors([]);
    } finally {
      setLoadingTopRated(false);
    }
  };
  useEffect(() => {
    fetchTopRatedVendors();
  }, []);

  // Fetch featured event vendors
  const fetchFeaturedVendors = async () => {
    setLoadingFeatured(true);
    setFeaturedError(null);
    try {
      const data = await get("/api/search/featured?location=&category=");
      if (data && data.data && Array.isArray(data.data.vendors)) {
        setFeaturedVendors(data.data.vendors);
      } else {
        setFeaturedVendors([]);
      }
    } catch (err: any) {
      setFeaturedError("Failed to load featured vendors.");
      setFeaturedVendors([]);
    } finally {
      setLoadingFeatured(false);
    }
  };
  useEffect(() => {
    fetchFeaturedVendors();
  }, []);

  // Fetch testimonials
  const fetchTestimonials = async () => {
    setLoadingTestimonials(true);
    setTestimonialsError(null);
    try {
      const data = await get("/api/reviews/testimonials?limit=");
      if (data && Array.isArray(data.data)) {
        setTestimonials(data.data);
      } else {
        setTestimonials([]);
      }
    } catch (err: any) {
      setTestimonialsError("Failed to load testimonials.");
      setTestimonials([]);
    } finally {
      setLoadingTestimonials(false);
    }
  };
  useEffect(() => {
    fetchTestimonials();
  }, []);

  // Fetch recent events
  const fetchRecentEvents = async () => {
    setLoadingEvents(true);
    setEventsError(null);
    try {
      const data = await get("/api/events/recent?limit=");
      if (data && Array.isArray(data.data)) {
        setRecentEvents(data.data);
      } else {
        setRecentEvents([]);
      }
    } catch (err: any) {
      setEventsError("Failed to load recent events.");
      setRecentEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };
  useEffect(() => {
    fetchRecentEvents();
  }, []);

  // Client logos (placeholder)
  const clientLogos = [
    { name: "Corporate Events Ltd", icon: "building" },
    { name: "Wedding Planners NG", icon: "heart" },
    { name: "Party Masters", icon: "party" },
    { name: "Photo Studios", icon: "camera" },
    { name: "Music Hub", icon: "music" },
    { name: "Luxury Transport", icon: "car" },
  ];

  return (
    <>
      {/* Hero Paid Advert Slider */}
      <div
        className="hero-slider-area"
        style={{
          position: "relative",
          width: "100%",
          height: "80vh",
          minHeight: 500,
          overflow: "hidden",
        }}
      >
        <style>{`
          @media (max-width: 768px) {
            .hero-slider-area {
              height: 50vh !important;
              min-height: 300px !important;
            }
            .hero-slider-area img {
              height: 50vh !important;
              min-height: 300px !important;
            }
            .hero-text-overlay h1 {
              font-size: 24px !important;
            }
            .hero-text-overlay p {
              font-size: 14px !important;
            }
            .hero-text-overlay button {
              padding: 8px 20px !important;
              font-size: 14px !important;
            }
            .hero-text-overlay {
              max-width: 90% !important;
              left: 5% !important;
              padding: 0 16px !important;
            }
          }
        `}</style>
        {loadingAdverts ? (
          <div
            style={{
              textAlign: "center",
              padding: 40,
              height: "80vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
            }}
          >
            Loading adverts...
          </div>
        ) : advertsError ? (
          <div
            style={{
              textAlign: "center",
              color: "red",
              padding: 40,
              height: "80vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
            }}
          >
            {advertsError}
          </div>
        ) : paidAdverts.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: 40,
              height: "80vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
            }}
          >
            No active adverts at the moment.
          </div>
        ) : (
          <Swiper
            modules={[Autoplay]}
            autoplay={{ delay: 4000 }}
            loop
            slidesPerView={1}
            style={{ width: "100%", height: "100%" }}
          >
            {paidAdverts.map((ad: any, idx: number) => (
              <SwiperSlide key={ad.id || idx}>
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    height: "80vh",
                  }}
                >
                  <img
                    src={ad.image}
                    alt={ad.name}
                    style={{
                      width: "100%",
                      height: "80vh",
                      objectFit: "cover",
                      objectPosition: "center",
                    }}
                  />
                  {/* Gradient overlay */}
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      background:
                        "linear-gradient(90deg, rgba(34,34,34,0.7) 0%, rgba(34,34,34,0.2) 70%, rgba(34,34,34,0.0) 100%)",
                    }}
                  />
                  {/* Text overlay */}
                  <div
                    className="hero-text-overlay"
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "7%",
                      transform: "translateY(-50%)",
                      color: "#fff",
                      zIndex: 2,
                      maxWidth: 500,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 600,
                        color: "#FFD700",
                        marginBottom: 8,
                      }}
                    >
                      {ad.service}
                    </div>
                    <h1
                      style={{
                        fontSize: 40,
                        fontWeight: 900,
                        margin: 0,
                        marginBottom: 12,
                        color: "#ffffff",
                        textShadow:
                          "0 4px 12px rgba(0,0,0,0.4), 0 2px 4px rgba(0,0,0,0.3)",
                        letterSpacing: "-0.5px",
                      }}
                    >
                      {ad.name}
                    </h1>
                    <p
                      style={{
                        fontSize: 20,
                        fontWeight: 400,
                        marginBottom: 24,
                        color: "#ffffff",
                        textShadow:
                          "0 2px 8px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)",
                        lineHeight: 1.6,
                      }}
                    >
                      {ad.description}
                    </p>
                    <button
                      style={{
                        background: "#FFD700",
                        color: "#222",
                        padding: "12px 32px",
                        borderRadius: 8,
                        fontWeight: 600,
                        textDecoration: "none",
                        fontSize: 18,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        cursor: "pointer",
                        border: "none",
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      }}
                      onClick={() => navigate("/subscription")}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 12px rgba(0,0,0,0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 2px 8px rgba(0,0,0,0.08)";
                      }}
                    >
                      Join Our Network
                    </button>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>

      {/* Search Bar below slider */}
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: 16,
          margin: "-40px auto 40px auto",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          maxWidth: 800,
          position: "relative",
          zIndex: 10,
        }}
      >
        <style>{`
          @media (max-width: 768px) {
            .search-bar-form {
              flex-direction: column !important;
              gap: 8px !important;
            }
            .search-bar-form input,
            .search-bar-form select,
            .search-bar-form button {
              width: 100% !important;
              font-size: 16px !important;
              padding: 12px 16px !important;
            }
          }
        `}</style>
        <form
          className="search-bar-form"
          style={{ display: "flex", alignItems: "center", gap: 12 }}
          onSubmit={(e) => {
            e.preventDefault();
            const params = new URLSearchParams();
            params.set("mode", "basic");
            if (searchQuery) params.append("query", searchQuery);
            if (selectedLocation) params.append("location", selectedLocation);
            navigate(`/vendors?${params.toString()}`);
          }}
        >
          <input
            type="text"
            placeholder="Search for vendors, services, or locations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 2,
              border: "none",
              padding: "14px 18px",
              fontSize: 18,
              outline: "none",
              borderRadius: 8,
            }}
          />
          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            style={{
              flex: 1,
              border: "none",
              padding: "14px 18px",
              fontSize: 18,
              borderRadius: 8,
              background: "#f8f8ff",
              color: selectedLocation ? "#333" : "#999",
            }}
          >
            <option value="">All Locations</option>
            <option value="Lagos">Lagos</option>
            <option value="Abuja">Abuja</option>
            <option value="Port Harcourt">Port Harcourt</option>
            <option value="Ibadan">Ibadan</option>
            <option value="Kano">Kano</option>
            <option value="Enugu">Enugu</option>
            <option value="Other">Other</option>
          </select>
          <button
            type="submit"
            style={{
              background: "#6C63FF",
              color: "#fff",
              border: "none",
              padding: "14px 32px",
              borderRadius: 8,
              fontWeight: 600,
              cursor: "pointer",
              fontSize: 18,
            }}
          >
            Search
          </button>
        </form>
      </div>

      {/* About / Mission / Vision Section */}
      <section
        style={{
          padding: "80px 0",
          background:
            "radial-gradient(circle at top left, #1e1b4b 0, #020617 45%, #020617 100%)",
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 20px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 32,
              marginBottom: 40,
              color: "#e5e7eb",
            }}
          >
            <p
              style={{
                textTransform: "uppercase",
                letterSpacing: 6,
                fontSize: 13,
                fontWeight: 700,
                color: "#a5b4fc",
              }}
            >
              Your event starts here.
            </p>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 24,
                alignItems: "flex-end",
                justifyContent: "space-between",
              }}
            >
              <h2
                style={{
                  fontSize: 40,
                  lineHeight: 1.2,
                  fontWeight: 800,
                  color: "#f9fafb",
                  maxWidth: 560,
                }}
              >
                Find your vendor. Find your vibe
              </h2>
              <p
                style={{
                  maxWidth: 460,
                  fontSize: 15,
                  lineHeight: 1.8,
                  color: "rgba(226,232,240,0.9)",
                }}
              >
                EventNova is the heartbeat of event discovery, a digital hub
                where innovation meets celebration. We connect people to the
                talent behind unforgettable moments, helping event vendors shine
                and helping clients find the perfect fit for their vision.
              </p>
            </div>
          </div>

          <div
            className="home-about-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 24,
            }}
          >
            {/* Brief About */}
            <div
              style={{
                background:
                  "linear-gradient(145deg, rgba(15,23,42,0.95), rgba(30,64,175,0.9))",
                borderRadius: 24,
                padding: 28,
                border: "1px solid rgba(129,140,248,0.4)",
                boxShadow: "0 22px 60px rgba(15,23,42,0.75)",
              }}
            >
              <h3
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: "#e5e7eb",
                  marginBottom: 10,
                }}
              >
                WHAT WE DO
              </h3>
              <p
                style={{
                  fontSize: 14,
                  lineHeight: 1.9,
                  color: "rgba(226,232,240,0.9)",
                }}
              >
                We make discovering event vendors effortless. From decorators to
                DJs, caterers to photographers, drone operators to sound
                engineers, we bring the entire events industry into one smart,
                beautifully curated space. No confusion. No stress. Just the
                right vendor, every time.
              </p>
            </div>

            {/* Mission */}
            <div
              style={{
                background:
                  "linear-gradient(145deg, rgba(15,23,42,0.95), rgba(124,58,237,0.9))",
                borderRadius: 24,
                padding: 28,
                border: "1px solid rgba(167,139,250,0.5)",
                boxShadow: "0 22px 60px rgba(15,23,42,0.7)",
              }}
            >
              <h3
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: "#e5e7eb",
                  marginBottom: 10,
                }}
              >
                HOW WE WORK
              </h3>
              <p
                style={{
                  fontSize: 14,
                  lineHeight: 1.9,
                  color: "rgba(226,232,240,0.9)",
                }}
              >
                We pair intuitive technology with real human creativity. Browse.
                Compare. Explore. Vendors showcase their craft; clients discover
                what inspires them. It’s event matchmaking, elevated.
              </p>
            </div>

            {/* Vision */}
            <div
              style={{
                background:
                  "linear-gradient(145deg, rgba(15,23,42,0.95), rgba(56,189,248,0.9))",
                borderRadius: 24,
                padding: 28,
                border: "1px solid rgba(125,211,252,0.5)",
                boxShadow: "0 22px 60px rgba(15,23,42,0.7)",
              }}
            >
              <h3
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: "#e5e7eb",
                  marginBottom: 10,
                }}
              >
                WHY WE EXIST
              </h3>
              <p
                style={{
                  fontSize: 14,
                  lineHeight: 1.9,
                  color: "rgba(226,232,240,0.9)",
                }}
              >
                Because events matter. Because memories deserve better than
                last-minute panic. Because talented vendors deserve to be seen.
                EventNova bridges the gap between dreams and the people who make
                them happen.
              </p>
            </div>
          </div>

          <style>{`
            @media (max-width: 992px) {
              .home-about-grid {
                grid-template-columns: 1fr !important;
              }
            }
          `}</style>
        </div>
      </section>

      {/* Statistics Section */}
      <div
        className="stats-area pb-60"
        style={{
          background: "#fff",
          borderBottom: "1px solid #eee",
          marginTop: "60px",
          paddingTop: "60px",
        }}
      >
        <style>{`
          @media (max-width: 768px) {
            .stats-area {
              margin-top: 30px !important;
              padding-top: 30px !important;
              padding-bottom: 30px !important;
            }
            .stat-number {
              font-size: 32px !important;
            }
            .stat-label {
              font-size: 14px !important;
            }
          }
        `}</style>
        <div className="container">
          <div
            style={{
              display: "flex",
              justifyContent: "space-around",
              flexWrap: "wrap",
              gap: 32,
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div
                className="stat-number"
                style={{
                  fontSize: 48,
                  fontWeight: 800,
                  color: "#6C63FF",
                  marginBottom: 8,
                }}
              >
                500+
              </div>
              <div
                className="stat-label"
                style={{ color: "#333", fontSize: 16, fontWeight: 500 }}
              >
                Verified Vendors
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                className="stat-number"
                style={{
                  fontSize: 48,
                  fontWeight: 800,
                  color: "#6C63FF",
                  marginBottom: 8,
                }}
              >
                1000+
              </div>
              <div
                className="stat-label"
                style={{ color: "#333", fontSize: 16, fontWeight: 500 }}
              >
                Events Completed
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                className="stat-number"
                style={{
                  fontSize: 48,
                  fontWeight: 800,
                  color: "#6C63FF",
                  marginBottom: 8,
                }}
              >
                98%
              </div>
              <div
                className="stat-label"
                style={{ color: "#333", fontSize: 16, fontWeight: 500 }}
              >
                Client Satisfaction
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                className="stat-number"
                style={{
                  fontSize: 48,
                  fontWeight: 800,
                  color: "#6C63FF",
                  marginBottom: 8,
                }}
              >
                24/7
              </div>
              <div
                className="stat-label"
                style={{ color: "#333", fontSize: 16, fontWeight: 500 }}
              >
                Support Available
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Indicators */}
      <div
        className="trust-indicators pb-40"
        style={{ background: "#f8f8ff", paddingTop: "60px" }}
      >
        <div className="container">
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <h3 style={{ color: "#222", fontWeight: 700, fontSize: 24 }}>
              Trusted by Leading Event Planners
            </h3>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 32,
            }}
          >
            {clientLogos.map((logo, idx) => (
              <div
                key={idx}
                style={{
                  background: "#fff",
                  padding: "16px 24px",
                  borderRadius: 12,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#333",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  transition: "all 0.3s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(0,0,0,0.1)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(0,0,0,0.06)";
                }}
              >
                {logo.icon === "building" && (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 21H21"
                      stroke="#6C63FF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M5 21V7L13 2L21 7V21"
                      stroke="#6C63FF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                    <path
                      d="M9 9V13H15V9"
                      stroke="#6C63FF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                  </svg>
                )}
                {logo.icon === "heart" && (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.19169 3.57831 3.16 4.61C2.1283 5.64169 1.54871 7.04097 1.54871 8.5C1.54871 9.95903 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7564 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22248 22.4518 8.5C22.4518 7.77752 22.3095 7.0621 22.0329 6.39464C21.7564 5.72718 21.351 5.12075 20.84 4.61Z"
                      stroke="#6C63FF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                  </svg>
                )}
                {logo.icon === "party" && (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2V6M12 18V22M4 12H8M16 12H20M19.07 19.07L16.24 16.24M19.07 4.93L16.24 7.76M4.93 19.07L7.76 16.24M4.93 4.93L7.76 7.76"
                      stroke="#6C63FF"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="3"
                      stroke="#6C63FF"
                      strokeWidth="2"
                      fill="none"
                    />
                  </svg>
                )}
                {logo.icon === "camera" && (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 4H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z"
                      stroke="#6C63FF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                    <circle
                      cx="12"
                      cy="13"
                      r="4"
                      stroke="#6C63FF"
                      strokeWidth="2"
                      fill="none"
                    />
                  </svg>
                )}
                {logo.icon === "music" && (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 18V5L21 3V16"
                      stroke="#6C63FF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                    <circle
                      cx="6"
                      cy="18"
                      r="3"
                      stroke="#6C63FF"
                      strokeWidth="2"
                      fill="none"
                    />
                    <circle
                      cx="18"
                      cy="16"
                      r="3"
                      stroke="#6C63FF"
                      strokeWidth="2"
                      fill="none"
                    />
                  </svg>
                )}
                {logo.icon === "car" && (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 3H5L7.68 14.39C7.77144 14.8504 8.02191 15.264 8.38755 15.5583C8.75318 15.8526 9.2107 16.009 9.68 16H19.4C19.8693 16.009 20.3268 15.8526 20.6925 15.5583C21.0581 15.264 21.3086 14.8504 21.4 14.39L23 8H6"
                      stroke="#6C63FF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                    <circle
                      cx="7"
                      cy="20"
                      r="2"
                      stroke="#6C63FF"
                      strokeWidth="2"
                      fill="none"
                    />
                    <circle
                      cx="17"
                      cy="20"
                      r="2"
                      stroke="#6C63FF"
                      strokeWidth="2"
                      fill="none"
                    />
                  </svg>
                )}
                {logo.name}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Service Categories Section */}
      <div
        className="service-categories-area pb-60"
        style={{ background: "#fff", paddingTop: "60px" }}
      >
        <div className="container">
          <div className="section-title text-center mb-40">
            <h2
              style={{
                color: "#222",
                fontWeight: 800,
                fontSize: 32,
                marginBottom: 8,
              }}
            >
              Browse by Service Category
            </h2>
            <p style={{ color: "#666", fontSize: 16, fontWeight: 400 }}>
              Discover vendors across all event service categories
            </p>
          </div>
          <div
            style={{
              display: "flex",
              overflowX: "auto",
              gap: 32,
              padding: "0 0 12px 0",
            }}
          >
            {loadingCategories ? (
              <div style={{ textAlign: "center", padding: 40, fontSize: 18 }}>
                Loading categories...
              </div>
            ) : categoriesError ? (
              <div style={{ textAlign: "center", color: "red", padding: 40 }}>
                {categoriesError}
              </div>
            ) : categories.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, fontSize: 18 }}>
                No categories found.
              </div>
            ) : (
              categories.map((cat, idx) => (
                <div
                  key={cat.id || idx}
                  style={{
                    minWidth: 200,
                    background: "#f8f8ff",
                    borderRadius: 16,
                    padding: 44,
                    textAlign: "center",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    height: 180,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 24px rgba(0,0,0,0.12)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 8px rgba(0,0,0,0.06)";
                  }}
                >
                  <CategoryIcon
                    categoryName={transformCategoryName(cat.name)}
                  />
                  <div
                    style={{
                      fontWeight: 700,
                      color: "#222",
                      fontSize: 20,
                      marginBottom: 8,
                    }}
                  >
                    {transformCategoryName(cat.name)}
                  </div>
                  <div
                    style={{
                      color: "#666",
                      fontSize: 16,
                      fontWeight: 400,
                      lineHeight: 1.5,
                      wordBreak: "break-word",
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {categorySubcategories[transformCategoryName(cat.name)]
                      ? categorySubcategories[
                          transformCategoryName(cat.name)
                        ].join(", ")
                      : cat.description}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Popular Cities Section */}
      <div
        className="popular-cities-area pb-60"
        style={{ background: "#f8f8ff", paddingTop: "60px" }}
      >
        <div className="container">
          <div className="section-title text-center mb-40">
            <h2
              style={{
                color: "#222",
                fontWeight: 800,
                fontSize: 32,
                marginBottom: 8,
              }}
            >
              Popular Cities
            </h2>
            <p style={{ color: "#666", fontSize: 16, fontWeight: 400 }}>
              Find vendors in your city
            </p>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 24,
              justifyContent: "center",
            }}
          >
            {loadingCities ? (
              <div style={{ textAlign: "center", padding: 40, fontSize: 18 }}>
                Loading cities...
              </div>
            ) : citiesError ? (
              <div style={{ textAlign: "center", color: "red", padding: 40 }}>
                {citiesError}
              </div>
            ) : (
              <>
                {popularCities.map((city, idx) => (
                  <div
                    key={city.name ?? idx}
                    style={{
                      flex: "1 1 200px",
                      maxWidth: 250,
                      background: "#fff",
                      borderRadius: 16,
                      overflow: "hidden",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 24px rgba(0,0,0,0.12)";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 8px rgba(0,0,0,0.06)";
                    }}
                  >
                    <img
                      src={
                        typeof city.image === "string" && city.image.trim()
                          ? city.image
                          : DEFAULT_CITY_CARD_IMAGE
                      }
                      alt={city.name ?? "City"}
                      style={{ width: "100%", height: 120, objectFit: "cover" }}
                    />
                    <div style={{ padding: 20 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          color: "#222",
                          fontSize: 18,
                          marginBottom: 4,
                        }}
                      >
                        {city.name}
                      </div>
                      <div style={{ color: "#666", fontSize: 14 }}>
                        {city.vendors} Vendors Available
                      </div>
                    </div>
                  </div>
                ))}
                {/* Show a placeholder if only one city is present */}
                {popularCities.length === 1 && (
                  <div
                    style={{
                      flex: "1 1 200px",
                      maxWidth: 250,
                      background: "#f0f0f0",
                      borderRadius: 16,
                      overflow: "hidden",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: 200,
                      padding: 20,
                    }}
                  >
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginBottom: 12, opacity: 0.6 }}
                    >
                      <path
                        d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z"
                        stroke="#888"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                      <circle
                        cx="12"
                        cy="10"
                        r="3"
                        stroke="#888"
                        strokeWidth="2"
                        fill="none"
                      />
                    </svg>
                    <div
                      style={{
                        color: "#666",
                        fontSize: 16,
                        fontWeight: 500,
                        textAlign: "center",
                      }}
                    >
                      More cities coming soon!
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div
        className="how-it-works-area pb-60"
        style={{ background: "#fff", paddingTop: "60px" }}
      >
        <div className="container">
          <div className="section-title text-center mb-40">
            <h2
              style={{
                color: "#222",
                fontWeight: 800,
                fontSize: 32,
                marginBottom: 8,
              }}
            >
              How EventNova Works
            </h2>
            <p style={{ color: "#666", fontSize: 16, fontWeight: 400 }}>
              Simple steps to find and book your perfect vendor
            </p>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 32,
            }}
          >
            <div
              style={{
                flex: "1 1 180px",
                maxWidth: 220,
                background: "#f8f8ff",
                borderRadius: 12,
                padding: 24,
                textAlign: "center",
                boxShadow: "0 2px 8px #eee",
                transition: "all 0.3s ease",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.12)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 8px #eee";
              }}
            >
              <div
                style={{
                  marginBottom: 12,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
              <div
                style={{
                  fontWeight: 700,
                  color: "#222",
                  fontSize: 16,
                  marginBottom: 8,
                }}
              >
                Browse Vendors
              </div>
              <div
                style={{
                  color: "#666",
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: 1.5,
                }}
              >
                Explore a wide range of event service providers.
              </div>
            </div>
            <div
              style={{
                flex: "1 1 180px",
                maxWidth: 220,
                background: "#f8f8ff",
                borderRadius: 12,
                padding: 24,
                textAlign: "center",
                boxShadow: "0 2px 8px #eee",
                transition: "all 0.3s ease",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.12)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 8px #eee";
              }}
            >
              <div
                style={{
                  marginBottom: 12,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                    <circle
                      cx="12"
                      cy="7"
                      r="4"
                      stroke="white"
                      strokeWidth="2"
                      fill="none"
                    />
                  </svg>
                </div>
              </div>
              <div
                style={{
                  fontWeight: 700,
                  color: "#222",
                  fontSize: 16,
                  marginBottom: 8,
                }}
              >
                View Profiles
              </div>
              <div
                style={{
                  color: "#666",
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: 1.5,
                }}
              >
                See vendor details, galleries, and reviews.
              </div>
            </div>
            <div
              style={{
                flex: "1 1 180px",
                maxWidth: 220,
                background: "#f8f8ff",
                borderRadius: 12,
                padding: 24,
                textAlign: "center",
                boxShadow: "0 2px 8px #eee",
                transition: "all 0.3s ease",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.12)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 8px #eee";
              }}
            >
              <div
                style={{
                  marginBottom: 12,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                  </svg>
                </div>
              </div>
              <div
                style={{
                  fontWeight: 700,
                  color: "#222",
                  fontSize: 16,
                  marginBottom: 8,
                }}
              >
                Engage & Chat
              </div>
              <div
                style={{
                  color: "#666",
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: 1.5,
                }}
              >
                Instantly message vendors to discuss your needs.
              </div>
            </div>
            <div
              style={{
                flex: "1 1 180px",
                maxWidth: 220,
                background: "#f8f8ff",
                borderRadius: 12,
                padding: 24,
                textAlign: "center",
                boxShadow: "0 2px 8px #eee",
                transition: "all 0.3s ease",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.12)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 8px #eee";
              }}
            >
              <div
                style={{
                  marginBottom: 12,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 6L9 17L4 12"
                      stroke="white"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="white"
                      strokeWidth="2"
                      fill="none"
                    />
                  </svg>
                </div>
              </div>
              <div
                style={{
                  fontWeight: 700,
                  color: "#222",
                  fontSize: 16,
                  marginBottom: 8,
                }}
              >
                Book with Confidence
              </div>
              <div
                style={{
                  color: "#666",
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: 1.5,
                }}
              >
                Securely book and review your favorite vendors.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Rated Vendors Section */}
      <div
        className="top-rated-vendors-area pb-60"
        style={{ background: "#f8f8ff", paddingTop: "60px" }}
      >
        <div className="container">
          <div className="section-title text-center mb-40">
            <h2
              style={{
                color: "#222",
                fontWeight: 800,
                fontSize: 32,
                marginBottom: 8,
              }}
            >
              Top Rated Vendors
            </h2>
            <p style={{ color: "#666", fontSize: 16, fontWeight: 400 }}>
              Handpicked vendors with exceptional ratings
            </p>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 32,
              justifyContent: "center",
            }}
          >
            {loadingTopRated ? (
              <div style={{ textAlign: "center", padding: 40, fontSize: 18 }}>
                Loading top rated vendors...
              </div>
            ) : topRatedError ? (
              <div style={{ textAlign: "center", color: "red", padding: 40 }}>
                {topRatedError}
              </div>
            ) : topRatedVendors.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, fontSize: 18 }}>
                No top rated vendors found.
              </div>
            ) : (
              topRatedVendors.map((vendor, idx) => (
                <div
                  key={vendor.id || idx}
                  style={{
                    flex: "1 1 280px",
                    maxWidth: 320,
                    background: "#fff",
                    borderRadius: 16,
                    padding: 24,
                    textAlign: "center",
                    boxShadow: "0 2px 8px #eee",
                    position: "relative",
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 24px rgba(0,0,0,0.12)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 2px 8px #eee";
                  }}
                >
                  {vendor.verified && (
                    <div
                      style={{
                        position: "absolute",
                        top: 16,
                        left: 16,
                        background: "#28a745",
                        color: "#fff",
                        borderRadius: 8,
                        padding: "4px 8px",
                        fontWeight: 700,
                        fontSize: 12,
                      }}
                    >
                      ✓ Verified
                    </div>
                  )}
                  {vendor.featured && (
                    <div
                      style={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                        background: "#FFD700",
                        color: "#fff",
                        borderRadius: 8,
                        padding: "4px 12px",
                        fontWeight: 700,
                        fontSize: 13,
                      }}
                    >
                      ★ Featured
                    </div>
                  )}
                  <img
                    src={
                      vendor.images && vendor.images.length > 0
                        ? vendor.images[0]
                        : ""
                    }
                    alt={vendor.name}
                    style={{
                      width: "100%",
                      height: 140,
                      objectFit: "cover",
                      borderRadius: 10,
                      marginBottom: 12,
                    }}
                  />
                  <h4 style={{ color: "#222", fontWeight: 700 }}>
                    {vendor.name}
                  </h4>
                  <span
                    style={{ color: "#666", fontSize: 15, fontWeight: 400 }}
                  >
                    {vendor.service} &bull; {vendor.location}
                  </span>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 4,
                      margin: "8px 0",
                    }}
                  >
                    <div style={{ color: "#FFD700", fontSize: 14 }}>★★★★★</div>
                    <span
                      style={{ color: "#333", fontSize: 12, fontWeight: 500 }}
                    >
                      {vendor.rating}
                    </span>
                  </div>
                  <p
                    style={{
                      color: "#333",
                      margin: "12px 0 20px",
                      fontSize: 15,
                      fontWeight: 400,
                      lineHeight: 1.5,
                    }}
                  >
                    {vendor.description}
                  </p>
                  <button
                    className="btn btn-primary"
                    style={{
                      background: "#444",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      padding: "8px 24px",
                      fontWeight: 600,
                      textDecoration: "none",
                      display: "inline-block",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      handleEngageVendor(vendor.id, navigate, setLoadingVendor)
                    }
                  >
                    Engage Vendor
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Featured Event Vendors Section */}
      <div
        id="vendor-carousel"
        className="collections-area pb-95"
        style={{ background: "#fff", paddingTop: "60px" }}
      >
        <div className="container">
          <div className="section-title text-center mb-50">
            <h2
              style={{
                color: "#222",
                fontWeight: 800,
                fontSize: 32,
                marginBottom: 8,
              }}
            >
              Featured Event Vendors
            </h2>
            <p
              style={{
                color: "#333",
                fontSize: 16,
                fontWeight: 400,
                lineHeight: 1.6,
              }}
            >
              Browse top-rated vendors and discover the perfect match for your
              event.
            </p>
          </div>
          {loadingFeatured ? (
            <div style={{ textAlign: "center", padding: 40, fontSize: 18 }}>
              Loading featured vendors...
            </div>
          ) : featuredError ? (
            <div style={{ textAlign: "center", color: "red", padding: 40 }}>
              {featuredError}
            </div>
          ) : featuredVendors.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, fontSize: 18 }}>
              No featured vendors found.
            </div>
          ) : (
            <Swiper
              modules={[Autoplay]}
              autoplay={{ delay: 3500 }}
              loop
              spaceBetween={30}
              slidesPerView={3}
              breakpoints={{
                320: { slidesPerView: 1 },
                768: { slidesPerView: 2 },
                1200: { slidesPerView: 3 },
              }}
              className="vendor-carousel"
            >
              {featuredVendors.map((vendor, idx) => (
                <SwiperSlide key={vendor.id || idx}>
                  <div
                    className="collection-product-2"
                    style={{
                      boxShadow: "0 4px 24px #eee",
                      borderRadius: 16,
                      background: "#fff",
                      padding: 24,
                      margin: 8,
                      minHeight: 480,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      transition: "all 0.3s ease",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 24px rgba(0,0,0,0.12)";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 4px 24px #eee";
                    }}
                  >
                    <img
                      src={vendor.image}
                      alt={vendor.name}
                      style={{
                        width: "100%",
                        height: 200,
                        objectFit: "cover",
                        borderRadius: 12,
                        marginBottom: 32,
                        paddingBottom: 24,
                      }}
                    />
                    <div
                      className="collection-content-2 text-center"
                      style={{ flex: 1 }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                          marginBottom: 8,
                        }}
                      >
                        <h4
                          style={{
                            color: "#222",
                            fontWeight: 700,
                            marginTop: 0,
                          }}
                        >
                          {vendor.name}
                        </h4>
                        {vendor.isVerified && (
                          <span style={{ color: "#28a745", fontSize: 16 }}>
                            ✓
                          </span>
                        )}
                        {vendor.isFeatured && (
                          <span style={{ color: "#FFD700", fontSize: 16 }}>
                            ★
                          </span>
                        )}
                      </div>
                      <span
                        style={{ color: "#666", fontSize: 15, fontWeight: 400 }}
                      >
                        {vendor.service} &bull; {vendor.location}
                      </span>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 4,
                          margin: "8px 0",
                        }}
                      >
                        <div style={{ color: "#FFD700", fontSize: 14 }}>
                          ★★★★★
                        </div>
                        <span
                          style={{
                            color: "#333",
                            fontSize: 12,
                            fontWeight: 500,
                          }}
                        >
                          {vendor.averageRating}
                        </span>
                      </div>
                      <p
                        style={{
                          color: "#333",
                          margin: "12px 0 20px",
                          fontSize: 15,
                          fontWeight: 400,
                          lineHeight: 1.5,
                        }}
                      >
                        {vendor.description}
                      </p>
                      {vendor.priceRange && (
                        <div
                          style={{
                            color: "#6C63FF",
                            fontWeight: 600,
                            marginBottom: 8,
                          }}
                        >
                          Price: {vendor.priceRange.min} -{" "}
                          {vendor.priceRange.max} {vendor.priceRange.currency}
                        </div>
                      )}
                      <button
                        className="btn btn-primary"
                        style={{
                          background:
                            "linear-gradient(135deg, #6C63FF 0%, #9D8DF1 100%)",
                          color: "#fff",
                          border: "none",
                          borderRadius: 8,
                          padding: "10px 28px",
                          fontWeight: 700,
                          textDecoration: "none",
                          display: "inline-block",
                          cursor: "pointer",
                          fontSize: 15,
                          boxShadow: "0 4px 12px rgba(108,99,255,0.3)",
                          transition:
                            "transform 0.2s ease, box-shadow 0.2s ease",
                        }}
                        onClick={() => navigate("/subscription")}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow =
                            "0 6px 20px rgba(108,99,255,0.4)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 12px rgba(108,99,255,0.3)";
                        }}
                      >
                        Join Our Network
                      </button>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </div>
      </div>

      {/* Vendor Call-to-Action Section */}
      <div
        className="vendor-cta-area pb-60"
        style={{ background: "#6C63FF", marginTop: "60px", paddingTop: "60px" }}
      >
        <style>{`
          @media (max-width: 768px) {
            .vendor-cta-area {
              margin-top: 30px !important;
              padding-top: 30px !important;
              padding-bottom: 30px !important;
            }
            .vendor-cta-content {
              padding: 24px 16px !important;
            }
            .vendor-cta-heading {
              font-size: 24px !important;
              text-align: center !important;
              padding: 0 16px !important;
            }
            .vendor-cta-text {
              font-size: 16px !important;
              padding: 0 16px !important;
            }
            .vendor-cta-button {
              font-size: 16px !important;
              padding: 12px 24px !important;
            }
          }
        `}</style>
        <div className="container">
          <div
            className="vendor-cta-content"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "48px 0",
            }}
          >
            <h2
              className="vendor-cta-heading"
              style={{
                color: "#fff",
                fontWeight: 800,
                marginBottom: 16,
                textAlign: "center",
              }}
            >
              Are you an Event Vendor?
            </h2>
            <p
              className="vendor-cta-text"
              style={{
                color: "#fff",
                fontSize: 18,
                maxWidth: 600,
                textAlign: "center",
                marginBottom: 24,
              }}
            >
              Join EventNova and showcase your services to thousands of clients
              looking for the best event experiences. Get discovered, engage
              instantly, and grow your business!
            </p>
            <a
              href="/subscription"
              className="btn btn-light vendor-cta-button"
              style={{
                background: "#fff",
                color: "#6C63FF",
                border: "none",
                borderRadius: 8,
                padding: "14px 40px",
                fontWeight: 700,
                fontSize: 18,
                textDecoration: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            >
              Subscribe Now
            </a>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div
        className="testimonials-area pb-60"
        style={{ background: "#f8f8ff", marginTop: "60px", paddingTop: "60px" }}
      >
        <div className="container">
          <div className="section-title text-center mb-40">
            <h2
              style={{
                color: "#222",
                fontWeight: 800,
                fontSize: 32,
                marginBottom: 8,
              }}
            >
              What Clients Say
            </h2>
            <p style={{ color: "#666", fontSize: 16, fontWeight: 400 }}>
              Real feedback from satisfied customers
            </p>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 32,
              justifyContent: "center",
            }}
          >
            {loadingTestimonials ? (
              <div style={{ textAlign: "center", padding: 40, fontSize: 18 }}>
                Loading testimonials...
              </div>
            ) : testimonialsError ? (
              <div style={{ textAlign: "center", color: "red", padding: 40 }}>
                {testimonialsError}
              </div>
            ) : testimonials.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, fontSize: 18 }}>
                No testimonials found.
              </div>
            ) : (
              testimonials.map((testimonial, idx) => (
                <div
                  key={testimonial.id || idx}
                  style={{
                    background: "#fff",
                    borderRadius: 16,
                    boxShadow: "0 2px 8px #eee",
                    padding: 32,
                    maxWidth: 340,
                    minWidth: 260,
                    flex: "1 1 260px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <img
                    src={
                      testimonial.avatar &&
                      testimonial.avatar !== "testimageurl"
                        ? testimonial.avatar
                        : "assets/img/default-avatar.png"
                    }
                    alt={testimonial.name}
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      marginBottom: 16,
                    }}
                  />
                  <div
                    style={{
                      color: "#222",
                      fontWeight: 700,
                      fontSize: 18,
                      marginBottom: 8,
                    }}
                  >
                    {testimonial.name}
                  </div>
                  <div
                    style={{
                      color: "#333",
                      fontSize: 15,
                      textAlign: "center",
                      fontWeight: 400,
                      lineHeight: 1.6,
                    }}
                  >
                    {testimonial.feedback}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Recent Events Section */}
      <div
        className="recent-events-area pb-60"
        style={{ background: "#fff", paddingTop: "60px" }}
      >
        <div className="container">
          <div className="section-title text-center mb-40">
            <h2
              style={{
                color: "#222",
                fontWeight: 800,
                fontSize: 32,
                marginBottom: 8,
              }}
            >
              Recent Vendors
            </h2>
            <p style={{ color: "#666", fontSize: 16, fontWeight: 400 }}>
              See what we've helped create
            </p>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 24,
              justifyContent: "center",
            }}
          >
            {loadingEvents ? (
              <div style={{ textAlign: "center", padding: 40, fontSize: 18 }}>
                Loading recent events...
              </div>
            ) : eventsError ? (
              <div style={{ textAlign: "center", color: "red", padding: 40 }}>
                {eventsError}
              </div>
            ) : recentEvents.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, fontSize: 18 }}>
                No recent events found.
              </div>
            ) : (
              recentEvents.map((event, idx) => {
                const imageSrc = resolveEventImage(event);
                return (
                  <div
                    key={event.id || idx}
                    style={{
                      flex: "1 1 280px",
                      maxWidth: 340,
                      background: "#f8f8ff",
                      borderRadius: 16,
                      padding: 0,
                      textAlign: "center",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      overflow: "hidden",
                      transition: "box-shadow 0.2s",
                      cursor: "pointer",
                      minHeight: 320,
                      height: 380,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.boxShadow =
                        "0 4px 24px rgba(0,0,0,0.12)")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.boxShadow =
                        "0 2px 8px rgba(0,0,0,0.06)")
                    }
                  >
                    <div
                      style={{
                        width: "100%",
                        height: 160,
                        marginTop: 16,
                        position: "relative",
                        borderRadius: 12,
                        overflow: "hidden",
                      }}
                    >
                      {imageSrc ? (
                        <>
                          <img
                            src={imageSrc}
                            alt={event.title}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              const placeholder = e.currentTarget
                                .nextElementSibling as HTMLElement | null;
                              if (placeholder)
                                placeholder.style.display = "flex";
                            }}
                          />
                          <div
                            className="event-image-placeholder"
                            style={{
                              display: "none",
                              alignItems: "center",
                              justifyContent: "center",
                              flexDirection: "column",
                              width: "100%",
                              height: "100%",
                              background:
                                "linear-gradient(135deg,#f8f8ff,#e0e7ff)",
                              color: "#6C63FF",
                              gap: 8,
                              fontWeight: 600,
                            }}
                          >
                            <svg
                              width="32"
                              height="32"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              style={{ opacity: 0.8 }}
                            >
                              <path
                                d="M4 4H20V20H4V4Z"
                                stroke="#6C63FF"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M4 16L9 11L13 15L16 12L20 16"
                                stroke="#6C63FF"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <circle
                                cx="9"
                                cy="9"
                                r="1.5"
                                stroke="#6C63FF"
                                strokeWidth="1.5"
                              />
                            </svg>
                            <span style={{ fontSize: 13, color: "#4a4a68" }}>
                              Image coming soon
                            </span>
                          </div>
                        </>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexDirection: "column",
                            width: "100%",
                            height: "100%",
                            background:
                              "linear-gradient(135deg,#f8f8ff,#e0e7ff)",
                            color: "#6C63FF",
                            gap: 8,
                            fontWeight: 600,
                          }}
                        >
                          <svg
                            width="32"
                            height="32"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ opacity: 0.8 }}
                          >
                            <path
                              d="M4 4H20V20H4V4Z"
                              stroke="#6C63FF"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M4 16L9 11L13 15L16 12L20 16"
                              stroke="#6C63FF"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <circle
                              cx="9"
                              cy="9"
                              r="1.5"
                              stroke="#6C63FF"
                              strokeWidth="1.5"
                            />
                          </svg>
                          <span style={{ fontSize: 13, color: "#4a4a68" }}>
                            Image coming soon
                          </span>
                        </div>
                      )}
                    </div>
                    <div style={{ padding: 20, paddingTop: 28 }}>
                      <div
                        style={{
                          color: "#222",
                          fontWeight: 700,
                          fontSize: 17,
                          marginBottom: 6,
                        }}
                      >
                        {event.title}
                      </div>
                      <div
                        style={{
                          color: "#333",
                          fontSize: 15,
                          fontWeight: 400,
                          lineHeight: 1.5,
                        }}
                      >
                        {event.story}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* About EventNova Banner Section (now last before footer) */}
      <section
        className="about-eventnova-section"
        style={{
          width: "100%",
          background: `linear-gradient(rgba(108,99,255,0.85), rgba(183,184,247,0.85)), url('https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=800&h=400&fit=crop'), url('https://images.unsplash.com/photo-1515169067865-5387a7e1e87b?w=1200&h=400&fit=crop')`,
          backgroundSize: "cover, 70%, cover",
          backgroundPosition: "center, center, center",
          backgroundRepeat: "no-repeat, no-repeat, no-repeat",
          backgroundBlendMode: "normal, lighten, multiply",
          padding: "80px 0 60px 0",
          textAlign: "center",
          color: "#fff",
          marginTop: 64,
          marginBottom: 120,
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <style>{`
          @media (max-width: 768px) {
            .about-eventnova-section {
              padding: 40px 16px !important;
              margin-top: 30px !important;
              margin-bottom: 60px !important;
              text-align: center !important;
            }
            .about-eventnova-content {
              text-align: center !important;
              padding: 0 16px !important;
            }
            .about-eventnova-heading {
              font-size: 28px !important;
              text-align: center !important;
            }
            .about-eventnova-subheading {
              font-size: 20px !important;
              text-align: center !important;
            }
            .about-eventnova-text {
              font-size: 16px !important;
              text-align: center !important;
            }
          }
        `}</style>
        {/* Decorative image removed as it's now blended in the background */}
        <div
          className="about-eventnova-content"
          style={{
            maxWidth: 700,
            margin: "0 auto",
            textAlign: "left",
            flex: 1,
            zIndex: 1,
          }}
        >
          <h2
            className="about-eventnova-heading"
            style={{
              fontWeight: 900,
              fontSize: 40,
              marginBottom: 18,
              color: "#ffffff",
              textShadow:
                "0 4px 12px rgba(0,0,0,0.4), 0 2px 4px rgba(0,0,0,0.3)",
              letterSpacing: "-0.5px",
            }}
          >
            About EventNova
          </h2>
          <h3
            className="about-eventnova-subheading"
            style={{
              fontWeight: 700,
              fontSize: 26,
              marginBottom: 16,
              color: "#ffffff",
              textShadow:
                "0 2px 8px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)",
            }}
          >
            Your Gateway to Memorable Events
          </h3>
          <p
            className="about-eventnova-text"
            style={{
              fontSize: 20,
              color: "#ffffff",
              maxWidth: 700,
              margin: 0,
              textShadow:
                "0 2px 8px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)",
              fontWeight: 400,
              lineHeight: 1.7,
            }}
          >
            EventNova is dedicated to connecting clients with the best event
            vendors across Nigeria. Whether you're planning a wedding, birthday,
            or corporate event, our platform makes it easy to discover, compare,
            and engage with trusted service providers.
          </p>
        </div>
      </section>

      {/* Search Types Selling Point Section */}
      <section
        className="search-types-section"
        style={{
          maxWidth: 1100,
          margin: "64px auto 0 auto",
          padding: 40,
          background: "#fff",
          borderRadius: 18,
          boxShadow: "0 4px 24px #eee",
          textAlign: "center",
        }}
      >
        <style>{`
          @media (max-width: 768px) {
            .search-types-section {
              padding: 24px 16px !important;
              margin: 30px 16px !important;
            }
            .search-types-heading {
              font-size: 24px !important;
            }
            .search-types-description {
              font-size: 16px !important;
              padding: 0 16px !important;
            }
            .search-types-button {
              font-size: 16px !important;
              padding: 12px 24px !important;
            }
          }
        `}</style>
        <h2
          className="search-types-heading"
          style={{
            color: "#6C63FF",
            fontWeight: 900,
            fontSize: 32,
            marginBottom: 18,
          }}
        >
          Find Vendors Your Way
        </h2>
        <p
          className="search-types-description"
          style={{
            color: "#333",
            fontSize: 18,
            marginBottom: 32,
            maxWidth: 700,
            marginLeft: "auto",
            marginRight: "auto",
            fontWeight: 400,
            lineHeight: 1.6,
          }}
        >
          EventNova offers three powerful ways to search for event
          vendors—tailored for every need, every event, and every user.
        </p>
        <div
          style={{
            display: "flex",
            gap: 32,
            flexWrap: "wrap",
            justifyContent: "center",
            marginBottom: 32,
          }}
        >
          <div
            style={{
              background: "#f8f8ff",
              borderRadius: 14,
              boxShadow: "0 2px 8px #e0e7ff",
              padding: 28,
              minWidth: 220,
              maxWidth: 320,
              textAlign: "center",
              flex: "1 1 220px",
            }}
          >
            <div
              style={{
                marginBottom: 12,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                }}
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
            <h3
              style={{
                color: "#222",
                fontWeight: 700,
                fontSize: 20,
                marginBottom: 8,
              }}
            >
              Normal Search
            </h3>
            <p
              style={{
                color: "#333",
                fontSize: 15,
                fontWeight: 400,
                lineHeight: 1.5,
              }}
            >
              Quickly find vendors by name, service, or location with a simple
              search bar.
            </p>
          </div>
          <div
            style={{
              background: "#f8f8ff",
              borderRadius: 14,
              boxShadow: "0 2px 8px #e0e7ff",
              padding: 28,
              minWidth: 220,
              maxWidth: 320,
              textAlign: "center",
              flex: "1 1 220px",
            }}
          >
            <div
              style={{
                marginBottom: 12,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                }}
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15V15.08H19.32Z"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
            <h3
              style={{
                color: "#222",
                fontWeight: 700,
                fontSize: 20,
                marginBottom: 8,
              }}
            >
              Advanced Search
            </h3>
            <p
              style={{
                color: "#333",
                fontSize: 15,
                fontWeight: 400,
                lineHeight: 1.5,
              }}
            >
              Filter by service, price, location, and guest capacity for precise
              results.
            </p>
          </div>
          <div
            style={{
              background: "#f8f8ff",
              borderRadius: 14,
              boxShadow: "0 2px 8px #e0e7ff",
              padding: 28,
              minWidth: 220,
              maxWidth: 320,
              textAlign: "center",
              flex: "1 1 220px",
            }}
          >
            <div
              style={{
                marginBottom: 12,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                }}
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2L2 7L12 12L22 7L12 2Z"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 17L12 22L22 17"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 12L12 17L22 12"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
            <h3
              style={{
                color: "#222",
                fontWeight: 700,
                fontSize: 20,
                marginBottom: 8,
              }}
            >
              AI Search
            </h3>
            <p
              style={{
                color: "#333",
                fontSize: 15,
                fontWeight: 400,
                lineHeight: 1.5,
              }}
            >
              Describe your needs in plain language and let our AI find the best
              matches for you.
            </p>
          </div>
        </div>
        <a
          href="/vendors"
          className="search-types-button"
          style={{
            background: "#6C63FF",
            color: "#fff",
            fontWeight: 700,
            fontSize: 18,
            padding: "14px 36px",
            borderRadius: 8,
            textDecoration: "none",
            boxShadow: "0 2px 8px #eee",
            transition: "background 0.2s, color 0.2s",
          }}
        >
          Try Vendor Search
        </a>
      </section>

      {/* Mobile App Download Section */}
      <section
        className="mobile-app-download-section"
        style={{
          margin: "80px auto 0 auto",
          padding: "80px 24px",
          borderRadius: 32,
          maxWidth: 1200,
          background:
            "linear-gradient(135deg, rgba(15,6,47,0.95), rgba(94,56,255,0.92))",
          color: "#fff",
          overflow: "hidden",
          position: "relative",
          boxShadow: "0 25px 80px rgba(30, 12, 99, 0.25)",
        }}
      >
        <style>{`
          @media (max-width: 992px) {
            .mobile-app-download-section {
              margin: 40px 16px !important;
              padding: 48px 20px !important;
            }
            .mobile-app-download-grid {
              flex-direction: column !important;
            }
            .mobile-app-device {
              margin-top: 40px !important;
            }
          }
          @media (max-width: 576px) {
            .mobile-app-download-headline {
              font-size: 28px !important;
            }
            .mobile-app-download-subtitle {
              font-size: 16px !important;
            }
            .mobile-app-store-badges a {
              width: 100% !important;
            }
          }
        `}</style>
        {/* Decorative elements */}
        <div
          style={{
            position: "absolute",
            width: 280,
            height: 280,
            background:
              "radial-gradient(circle, rgba(255,255,255,0.18), transparent)",
            top: -80,
            right: -60,
            borderRadius: "50%",
            filter: "blur(0px)",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: 160,
            height: 160,
            background: "rgba(255, 255, 255, 0.08)",
            borderRadius: 32,
            bottom: -40,
            left: 60,
            transform: "rotate(25deg)",
          }}
        />
        <div
          className="mobile-app-download-grid"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 48,
            position: "relative",
            zIndex: 2,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 16px",
                borderRadius: 999,
                background: "rgba(255,255,255,0.12)",
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: 1,
                textTransform: "uppercase",
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#29f19c",
                  boxShadow: "0 0 8px #29f19c",
                }}
              />
              Now on iOS & Android
            </span>
            <h2
              className="mobile-app-download-headline"
              style={{
                fontSize: 42,
                fontWeight: 900,
                margin: "24px 0 16px",
                lineHeight: 1.2,
                color: "#fff",
                textShadow: "0 8px 30px rgba(0,0,0,0.35)",
              }}
            >
              Manage your events on the go with the EventNova app.
            </h2>
            <p
              className="mobile-app-download-subtitle"
              style={{
                fontSize: 18,
                lineHeight: 1.7,
                color: "rgba(255,255,255,0.9)",
                maxWidth: 580,
                marginBottom: 28,
              }}
            >
              Track conversations, confirm bookings, and get real-time alerts in
              a beautifully crafted experience built for planners, vendors, and
              event lovers who never settle for less.
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 16,
                marginBottom: 32,
              }}
            >
              {[
                "Instant chat & booking notifications",
                "Offline-ready itinerary access",
                "Secure payments & smart reminders",
              ].map((feature) => (
                <div
                  key={feature}
                  style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "flex-start",
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 999,
                      background: "rgba(41, 241, 156, 0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#29f19c",
                      fontWeight: 700,
                    }}
                  >
                    ✓
                  </div>
                  <span style={{ fontSize: 15, lineHeight: 1.5 }}>
                    {feature}
                  </span>
                </div>
              ))}
            </div>
            <div
              className="mobile-app-store-badges"
              style={{
                display: "flex",
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              <a
                href="https://play.google.com/store/apps/details?id=com.eventnova"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 28px",
                  borderRadius: 16,
                  border: "1px solid rgba(255,255,255,0.3)",
                  background: "rgba(255,255,255,0.08)",
                  color: "#fff",
                  textDecoration: "none",
                  transition: "transform 0.2s, border-color 0.2s",
                  fontWeight: 600,
                  minWidth: 230,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.6)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)";
                }}
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 512 512"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M325.3 234.3L104.6 13.6C148.6-4.5 199.9-4.7 244.2 13.2L437.4 91.7C461.7 101.7 482.1 118.8 496.2 141.1L325.3 234.3Z"
                    fill="#FFD700"
                  />
                  <path
                    d="M104.6 13.6C76.6 24.8 52.4 45.1 35.1 72.5C12.5 107.7 0 149.1 0 192V320C0 362.9 12.5 404.3 35.1 439.5C52.4 467 76.6 487.3 104.6 498.4L325.3 277.7L104.6 57.1V13.6Z"
                    fill="#4facfe"
                  />
                  <path
                    d="M325.3 277.7L496.2 370.9C482.1 393.2 461.7 410.3 437.4 420.3L244.2 498.8C199.9 516.7 148.6 516.5 104.6 498.4L325.3 277.7Z"
                    fill="#f093fb"
                  />
                </svg>
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>Get it on</div>
                  <div style={{ fontSize: 18 }}>Google Play</div>
                </div>
              </a>
              <a
                href="https://apps.apple.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 28px",
                  borderRadius: 16,
                  border: "1px solid rgba(255,255,255,0.3)",
                  background: "rgba(255,255,255,0.08)",
                  color: "#fff",
                  textDecoration: "none",
                  transition: "transform 0.2s, border-color 0.2s",
                  fontWeight: 600,
                  minWidth: 230,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.6)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)";
                }}
              >
                <svg
                  width="26"
                  height="26"
                  viewBox="0 0 256 315"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M210.9 167.4C210.9 219.9 253 249.2 253.4 249.4C233.6 305.2 193 315 182.2 315C168.4 315 144.2 305.8 120.6 305.8C96.4 305.8 69.4 315.3 56.8 315.3C27.1 315.3 0 284.2 0 241.5C0 199.2 25.2 154.4 54.5 154.4C71.7 154.4 88.5 165.8 103.2 165.8C116.8 165.8 137.5 154.4 159.7 154.4C213.5 154.5 229.3 167.4 229.3 167.4H210.9Z"
                    fill="#fff"
                  />
                  <path
                    d="M175.9 105.2C185.7 94.7 192.4 80.6 192.4 66.2C192.4 62.9 191.9 59.6 190.8 57C177.8 57.5 161.9 66 152 76.5C143.4 85.8 136.2 100.4 136.2 114.7C136.2 118.2 136.7 121.7 138 125.1C151.7 125.5 166.7 116 175.9 105.2Z"
                    fill="#fff"
                  />
                </svg>
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>Download on</div>
                  <div style={{ fontSize: 18 }}>App Store</div>
                </div>
              </a>
            </div>
          </div>
          <div
            className="mobile-app-feature-grid"
            style={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 18,
            }}
          >
            {[
              {
                title: "Unified Inbox",
                desc: "Track all vendor chats and negotiations in one clean feed.",
              },
              {
                title: "Smart Alerts",
                desc: "Instant push notifications for bookings, payments, and changes.",
              },
              {
                title: "Offline Access",
                desc: "Download itineraries and timelines for venues with weak signal.",
              },
              {
                title: "Smart Search Suite",
                desc: "Toggle between Normal, Advanced, and AI Search to find vendors the exact way you prefer.",
              },
              {
                title: "Secure Payments",
                desc: "Approve quotes and release vendor payments with biometric auth.",
              },
              {
                title: "Performance Insights",
                desc: "Monitor response rates, satisfaction scores, and revenue trends.",
              },
            ].map((feature) => (
              <div
                key={feature.title}
                style={{
                  background: "rgba(255,255,255,0.08)",
                  borderRadius: 20,
                  padding: "24px 22px",
                  border: "1px solid rgba(255,255,255,0.12)",
                  minHeight: 160,
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  boxShadow: "0 15px 40px rgba(3, 6, 25, 0.25)",
                }}
              >
                <span
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 12,
                    background: "rgba(255,255,255,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    color: "#FFD700",
                  }}
                >
                  •
                </span>
                <div style={{ fontSize: 18, fontWeight: 700 }}>
                  {feature.title}
                </div>
                <p
                  style={{
                    fontSize: 14,
                    lineHeight: 1.6,
                    color: "rgba(255,255,255,0.85)",
                    margin: 0,
                  }}
                >
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {loadingVendor && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(255,255,255,0.6)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 32,
            color: "#6C63FF",
            fontWeight: 700,
          }}
        >
          Loading vendor details...
        </div>
      )}
    </>
  );
}

export default Home;
